import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, Button, Card, Input, Label, StatusChip, fmtMoney } from '@smena/ui';
import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { createAccount, fetchAccounts, type AccountListItem } from '@/features/accounts/api';

export function AccountsPage() {
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['accounts'], queryFn: fetchAccounts, staleTime: 5_000 });

  return (
    <>
      <PageHeader
        title="Аккаунты"
        subtitle="Все аккаунты Smena. Клик по названию - карточка с траблшутингом."
        actions={<Button onClick={() => setCreating(true)}>Создать аккаунт</Button>}
      />
      <QueryState
        isLoading={q.isLoading}
        error={q.error}
        isEmpty={q.data?.length === 0}
        emptyText="Аккаунтов пока нет"
      >
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wider text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Аккаунт</th>
                <th className="px-4 py-2 font-medium">Тип</th>
                <th className="px-4 py-2 font-medium">Тариф</th>
                <th className="px-4 py-2 text-right font-medium">Объекты</th>
                <th className="px-4 py-2 text-right font-medium">Брони</th>
                <th className="px-4 py-2 text-right font-medium">Диалоги</th>
                <th className="px-4 py-2 text-right font-medium">Эскалации</th>
                <th className="px-4 py-2 font-medium">Здоровье</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.map((a) => (
                <tr key={a.id} className="border-b border-line/60 last:border-0 hover:bg-panel2">
                  <td className="px-4 py-2">
                    <Link to={`/accounts/${a.id}`} className="text-accent-fg hover:underline">
                      {a.name}
                    </Link>
                    <div className="font-mono text-[11px] text-muted">{a.slug}</div>
                  </td>
                  <td className="px-4 py-2">
                    <StatusChip tone={a.kind === 'demo' ? 'muted' : 'accent'}>{a.kind}</StatusChip>
                  </td>
                  <td className="px-4 py-2">{planLabel(a)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.counters.properties}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.counters.bookings}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.counters.sessions}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {a.counters.escalationsOpen > 0 ? (
                      <span className="text-amber">{a.counters.escalationsOpen}</span>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <HealthChips item={a} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </QueryState>
      {creating ? <CreateAccountModal onClose={() => setCreating(false)} /> : null}
    </>
  );
}

function planLabel(a: AccountListItem): string {
  const { base_rub: base, per_property_rub: per } = a.plan;
  if (base === undefined && per === undefined) return '-';
  const parts: string[] = [];
  if (base !== undefined) parts.push(fmtMoney(base));
  if (per !== undefined) parts.push(`+${fmtMoney(per)}/об.`);
  return parts.join(' ');
}

/** Здоровье = список того, что требует внимания. Пусто - зелёный чип «ок». */
function HealthChips({ item }: { item: AccountListItem }) {
  const problems: string[] = [];
  if (item.health.channelsError) problems.push(`каналы: ${item.health.channelsError}`);
  if (item.health.icalError) problems.push(`iCal: ${item.health.icalError}`);
  if (item.health.avitoStatus && item.health.avitoStatus !== 'active') {
    problems.push(`Avito: ${item.health.avitoStatus}`);
  }
  if (!item.setup?.completedAt) problems.push(`онбординг: шаг ${item.setup?.currentStep ?? 1}`);

  if (!problems.length) return <StatusChip tone="green">ок</StatusChip>;
  return (
    <div className="flex flex-wrap gap-1">
      {problems.map((p) => (
        <StatusChip key={p} tone="amber">
          {p}
        </StatusChip>
      ))}
    </div>
  );
}

function CreateAccountModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'client' | 'demo'>('client');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: createAccount,
    onSuccess: (r) => {
      setCreated(r.owner);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({ slug, name, kind, ownerEmail, ownerName });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать аккаунт');
    }
  }

  // Пароль owner-а сервер отдаёт ОДИН раз: пока модалка открыта - других шансов его увидеть нет.
  if (created) {
    return (
      <Modal title="Аккаунт создан" onClose={onClose}>
        <p className="mb-3 text-sm text-muted">
          Передайте клиенту доступ. Пароль показывается один раз - в базе только хеш.
        </p>
        <div className="rounded-md border border-line bg-panel2 p-3 font-mono text-sm">
          <div>{created.email}</div>
          <div className="text-accent-fg">{created.password}</div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(`${created.email} / ${created.password}`);
              toast.success('Скопировано');
            }}
          >
            Скопировать
          </Button>
          <Button onClick={onClose}>Готово</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Новый аккаунт" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field
          label="Название УК"
          value={name}
          onChange={setName}
          placeholder="Апартаменты на Невском"
          required
        />
        <Field
          label="Slug (латиница, для URL)"
          value={slug}
          onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          placeholder="nevsky-apart"
          required
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="kind">Тип</Label>
          <select
            id="kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'client' | 'demo')}
            className="h-10 rounded-md border border-line-control bg-input px-3 text-sm text-text"
          >
            <option value="client">client - боевой аккаунт УК</option>
            <option value="demo">demo - стенд, можно заводить брони руками</option>
          </select>
        </div>
        <div className="my-1 border-t border-line" />
        <Field
          label="Email владельца"
          value={ownerEmail}
          onChange={setOwnerEmail}
          placeholder="owner@uk.ru"
          required
        />
        <Field
          label="Имя владельца"
          value={ownerName}
          onChange={setOwnerName}
          placeholder="Иван Петров"
        />
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Создаём…' : 'Создать'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
